/* eslint-disable camelcase */
import { configure, extend, localize } from 'vee-validate';
import en from 'vee-validate/dist/locale/en.json';
import pl from 'vee-validate/dist/locale/pl.json';
import es from 'vee-validate/dist/locale/es.json';
import uk from 'vee-validate/dist/locale/uk.json';
import ru from 'vee-validate/dist/locale/ru.json';
import sl from 'vee-validate/dist/locale/sl.json';
import { i18n } from '@/plugins/i18n';

import {
    between,
    double,
    email,
    integer,
    is_not,
    max,
    max_value,
    min,
    min_value,
    required
} from 'vee-validate/dist/rules';
import {
    IS_CURRENT_DATE_LESS_OR_EQUAL,
    IS_DATE_GREATER,
    IS_DATE_GREATER_OR_EQUAL,
    IS_TIME_GREATER_THAN_NOW,
    GET_SECONDS_FROM_TIME
} from '@/helpers/dates';

configure({
    mode: 'eager'
});

localize({
    en,
    pl,
    es,
    uk,
    ru,
    sl
});

extend('is_not', {
    ...is_not
});
extend('required', {
    ...required
});
extend('double', {
    ...double
});
extend('between', {
    ...between
});

extend('email', {
    ...email
});

extend('integer', {
    ...integer
});

extend('minValue', {
    ...min_value
});

extend('maxValue', {
    ...max_value
});

extend('password', {
    params: ['target'],
    validate (value, { target }) {
        return value === target;
    },
    message: () => i18n.t('custom_validation.pass_match_error')
});
extend('min', {
    ...min
});
extend('min_value', {
    ...min_value
});
extend('max', {
    ...max
});
extend('decimal', {
    validate: value => {
        const regex = /^[0-9]+(\.[0-9]{1,})?$/i;
        return regex.test(value);
    }
});

extend('coordinates', {
    validate: value => {
        const regex = /^(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)$/i;
        return regex.test(value);
    }
});

extend('isCurrentDateLessOrEqual', {
    validate: value => {
        return IS_CURRENT_DATE_LESS_OR_EQUAL(new Date(value));
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isDateGreaterOrEqual', {
    params: ['target'],
    validate: (value, { target }) => {
        return IS_DATE_GREATER_OR_EQUAL(new Date(value), new Date(target));
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isDateGreaterThanNow', {
    params: ['target'],
    validate: (value, { target }) => {
        return IS_DATE_GREATER(new Date(value), new Date());
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isDateGreaterOrEqualNow', {
    validate: (value) => {
        return IS_DATE_GREATER_OR_EQUAL(new Date(value), new Date());
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isTimeGreaterThanNow', {
    params: ['target'],
    validate: (value, { target }) => {
        return IS_DATE_GREATER(new Date(target), new Date()) || IS_TIME_GREATER_THAN_NOW(value);
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isDateTimeGreaterThan', {
    params: ['targetTime', 'targetDate', 'thisDate'],
    validate: (value, { targetTime, targetDate, thisDate }) => {
        return IS_DATE_GREATER_OR_EQUAL(new Date(thisDate), new Date(targetDate)) && (
            IS_DATE_GREATER(new Date(thisDate), new Date(targetDate)) || value > targetTime
        );
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isDateTimeGreaterThanNow', {
    params: ['target'],
    validate: (value, { target }) => {
        if (!target) return true;
        return IS_DATE_GREATER_OR_EQUAL(new Date(target), new Date()) && (
            IS_DATE_GREATER(new Date(target), new Date()) || IS_TIME_GREATER_THAN_NOW(value)
        );
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isTimeLessOrEqualThan', {
    params: ['target'],
    validate: (value, { target }) => {
        return GET_SECONDS_FROM_TIME(value) <= GET_SECONDS_FROM_TIME(target);
    },
    message: () => i18n.t('custom_validation.date_range_error')
});

extend('isDateAllowed', {
    params: ['fn'],
    validate: (value, { fn }) => {
        return fn(value);
    },
    message: () => i18n.t('custom_validation.date_not_allowed')
});

extend('phone', {
    validate: value => {
        const regex = /^[0-9+\-\s]+/i;
        return regex.test(value);
    },
    message: () => i18n.t('custom_validation.phone_error')
});
extend('includesDefault', {
    validate: value => {
        if (Array.isArray(value)) {
            return value.some(f => f.isDefault);
        } else return false;
    },
    message: () => i18n.t('custom_validation.includes_default')
});

extend('capitalLetter', {
    validate: value => {
        const regex = /[\p{Lu}]+/u;
        return regex.test(value);
    },
    message: () => i18n.t('custom_validation.capital_letter')
});

extend('specialCharacter', {
    validate: value => {
        const regex = /[!-/:-@[-`{-~]+/u;
        return regex.test(value);
    },
    message: () => i18n.t('custom_validation.special_character')
});

extend('requiredDuration', {
    validate: (value) => {
        return value && value.HH !== '' && value.mm !== '';
    },
    message: () => i18n.t('custom_validation.duration_required')
});

extend('durationGreaterThan', {
    params: ['target'],
    validate: (value, { target }) => {
        return value && target && `${value.HH}${value.mm}` >= `${target.HH}${target.mm}`;
    },
    message: () => i18n.t('custom_validation.duration_error')
});

extend('areSpacesBetweenTimesValid', {
    params: ['durationInSeconds'],
    validate: (value, { durationInSeconds }) => {
        if (!Array.isArray(value) || value.length <= 1) return true;

        const minDifferenceInSeconds = Number(durationInSeconds);
        if (!Number.isFinite(minDifferenceInSeconds) || minDifferenceInSeconds <= 0) return true;

        const timesInSeconds = value
            .map(time => {
                if (typeof time !== 'string') return NaN;
                return GET_SECONDS_FROM_TIME(time);
            });

        if (timesInSeconds.some(time => Number.isNaN(time))) return false;

        for (let i = 1; i < timesInSeconds.length; i++) {
            if ((
                timesInSeconds[i] - timesInSeconds[i - 1]) < minDifferenceInSeconds) {
                return false;
            }
        }

        return true;
    },
    message: () => i18n.t('custom_validation.repetition_hours_error')
});

extend('areTimesInRange', {
    params: ['startTime', 'endTime'],
    validate: (value, { startTime, endTime }) => {
        if (!Array.isArray(value)) return true;

        const startInSeconds = GET_SECONDS_FROM_TIME(startTime);
        const endInSeconds = GET_SECONDS_FROM_TIME(endTime);
        const timeInSeconds = value.map(time => GET_SECONDS_FROM_TIME(time));

        if (endInSeconds <= startInSeconds) {
            return timeInSeconds.every(time => time >= startInSeconds || time <= endInSeconds);
        } else {
            return timeInSeconds.every(time => time >= startInSeconds && time <= endInSeconds);
        }
    },
    message: () => i18n.t('custom_validation.times_in_range_error')
});

extend('max_length', {
    params: ['maxLength'],
    validate: (value, { maxLength }) => {
        return value.length <= maxLength;
    },
    message: () => i18n.t('custom_validation.max_length_error')
});
